import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="bg-white dark:bg-slate-950 border-t border-gray-200 dark:border-amber-600/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <Link to="/" className="inline-block mb-6 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_7525935c-c254-4a17-91ab-7c1c6aa37782/artifacts/wswalrd7_CopperAxis%20Logo%20HD.png"
                alt="CopperAxis"
                className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-110 rounded-lg"
              />
            </Link>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed font-bold mb-3">
              Strength. Structure. Supply.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_coppertrade/artifacts/k2rn5di1_MMIPL%20Logo.jpg"
                alt="Mehra Mac Industries"
                className="h-12 w-auto object-contain"
              />
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 uppercase">Powered by</span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Mehra Mac Industries Pvt. Ltd.</span>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
              Premium copper solutions engineered for industrial excellence.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a 
                href="https://www.linkedin.com/company/copperaxis/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-gray-100 dark:bg-slate-800 rounded-lg hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-600 dark:text-gray-400 text-sm hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <a href="/#products" className="text-gray-600 dark:text-gray-400 text-sm hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  Products
                </a>
              </li>
              <li>
                <a href="/#industries" className="text-gray-600 dark:text-gray-400 text-sm hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  Industries
                </a>
              </li>
              <li>
                <Link to="/about" className="text-gray-600 dark:text-gray-400 text-sm hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 dark:text-gray-400 text-sm hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="text-amber-400 w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="text-gray-600 dark:text-gray-400 text-sm space-y-2">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 font-medium">Operations and Sales</p>
                    <div className="font-semibold">9093 34 35 36</div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-500 font-medium">Sales and Business Development</p>
                    <div className="font-semibold">9246 52 31 53</div>
                  </div>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="text-amber-400 w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href="mailto:mehramac@gmail.com" className="text-gray-600 dark:text-gray-400 text-sm hover:text-amber-400 transition-colors">
                  mehramac@gmail.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="text-amber-400 w-5 h-5 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Jeedimetla IDA, Phase III,<br />Hyderabad 500055
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-amber-600/20 mt-8 pt-8 text-center">
          <p className="text-gray-500 dark:text-gray-500 text-sm">
            © {new Date().getFullYear()} Mehra Mac Industries Pvt. Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;